import React, { useState, useRef, DragEvent, ChangeEvent } from 'react';
import { Upload, FileText, AlertCircle, CheckCircle, Loader2 } from 'lucide-react';
import { Patient } from '../types/patient';
import { transformEhrToPatient } from '../utils/transformEhrToPatient';
import { parseXmlToJson } from '../utils/parseXmlToJson';
import { parseAthenaCcdaXml, isCcdaFormat } from '../utils/athenaXmlParser';
import {
  isPreloadedPatient,
  getPatientIdentifier,
  loadSupplementalData,
  mergeSupplementalData,
  isValidFilenameFormat,
  extractPatientNameFromFilename,
} from '../utils/dataStoreLoader';
import {
  defaultWellbeingFactors,
  generateDefaultAppleHealthData,
  defaultHealthPlan
} from '../data/defaultPatientData';

interface FileUploadProps {
  onPatientUploaded: (patient: Patient) => void;
}

type UploadStatus = 'idle' | 'uploading' | 'success' | 'error';

export default function FileUpload({ onPatientUploaded }: FileUploadProps) {
  const [isDragActive, setIsDragActive] = useState(false);
  const [status, setStatus] = useState<UploadStatus>('idle');
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [fileName, setFileName] = useState<string>('');
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleDragEnter = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragActive(true);
  };

  const handleDragLeave = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragActive(false);
  };

  const handleDragOver = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragActive(false);

    const files = e.dataTransfer.files;
    if (files && files.length > 0) {
      processFile(files[0]);
    }
  };

  const handleFileInput = (e: ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      processFile(files[0]);
    }
  };

  const processFile = async (file: File) => {
    // Validate file type (XML or PDF)
    if (!file.name.endsWith('.xml') && !file.name.endsWith('.pdf')) {
      setStatus('error');
      setErrorMessage('Please upload an XML or PDF file');
      return;
    }

    // Validate filename format (firstname_lastname.xml or firstname_lastname.pdf)
    if (!isValidFilenameFormat(file.name)) {
      setStatus('error');
      setErrorMessage('Invalid filename format. Please use format: firstname_lastname.xml (e.g., john_smith.xml)');
      return;
    }

    setFileName(file.name);
    setStatus('uploading');
    setErrorMessage('');

    try {
      // Read the file content
      const xmlContent = await file.text();

      let patient: Patient;

      // Check if this is a CCDA format XML (Athena EHR)
      if (isCcdaFormat(xmlContent)) {
        console.log('Detected CCDA format XML, using Athena parser');

        // Parse basic patient demographics from CCDA XML
        const ccdaData = parseAthenaCcdaXml(xmlContent);

        // Create a minimal patient object with CCDA data
        patient = {
          id: `uploaded-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
          firstName: ccdaData.firstName,
          lastName: ccdaData.lastName,
          dateOfBirth: ccdaData.dateOfBirth,
          mrn: ccdaData.mrn,
          lastVisit: new Date().toISOString().split('T')[0],
          riskLevel: 'low',
          healthMetrics: {
            obesityAnalysis: {
              bmi: 24,
              vo2Max: 40,
              bodyFatPercentage: 20,
              muscleMassIndex: 35,
              risk: 'low'
            },
            metabolicAnalysis: {
              risk: 'low'
            },
            cholesterolAnalysis: {
              ldl: 100,
              hdl: 50,
              total: 180,
              risk: 'low'
            },
            cardiovascularAnalysis: {
              systolic: 120,
              diastolic: 80,
              heartRate: 70,
              respiratoryRate: 16,
              risk: 'low'
            }
          },
          wellbeingFactors: defaultWellbeingFactors,
          labResults: [],
          medications: [],
          allergies: [],
          appleHealthData: generateDefaultAppleHealthData(),
          plans: defaultHealthPlan,
          medicalTimeline: {},
          historicalData: {}
        };
      } else {
        // Use existing eventstream parser for non-CCDA XMLs
        let parsedData;

        // Try API first, fall back to client-side parsing
        try {
          const response = await fetch('/api/parse', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({ xml: xmlContent }),
          });

          if (response.ok) {
            const contentType = response.headers.get('content-type');
            if (contentType && contentType.includes('application/json')) {
              parsedData = await response.json();
            } else {
              throw new Error('API returned non-JSON response');
            }
          } else {
            throw new Error('API request failed');
          }
        } catch {
          // Fall back to client-side parsing
          console.log('API unavailable, using client-side parsing');
          parsedData = parseXmlToJson(xmlContent);
        }

        // Transform to Patient format
        patient = transformEhrToPatient(parsedData);
      }

      // Extract patient name from filename and override if available
      const extractedName = extractPatientNameFromFilename(file.name);
      if (extractedName) {
        patient.firstName = extractedName.firstName;
        patient.lastName = extractedName.lastName;
        console.log(`Extracted patient name from filename: ${extractedName.firstName} ${extractedName.lastName}`);
      }

      // Check if this is a pre-loaded patient and merge supplemental data
      if (isPreloadedPatient(file.name)) {
        const patientId = getPatientIdentifier(file.name);
        if (patientId) {
          console.log(`Detected pre-loaded patient: ${patientId}`);
          try {
            const supplementalData = await loadSupplementalData(patientId);
            patient = mergeSupplementalData(patient, supplementalData);
            console.log('Merged supplemental data:', supplementalData);
          } catch (e) {
            console.warn('Failed to load supplemental data, continuing with XML data only:', e);
          }
        }
      }

      // Store the file reference for AI insights generation
      patient.ehrFileReference = file.name;

      // Store the original XML content in localStorage for AI insights generation
      // Use the patient ID as the key once we have it
      try {
        localStorage.setItem(`ehr_content_${patient.id}`, xmlContent);
      } catch (e) {
        console.error('Failed to store EHR content in localStorage:', e);
      }

      setStatus('success');

      // Notify parent component
      setTimeout(() => {
        onPatientUploaded(patient);
        // Reset state after a brief delay
        setTimeout(() => {
          setStatus('idle');
          setFileName('');
        }, 1000);
      }, 500);

    } catch (err) {
      setStatus('error');
      setErrorMessage(err instanceof Error ? err.message : 'An error occurred while processing the file');
    }
  };

  const handleClick = () => {
    fileInputRef.current?.click();
  };

  const resetUpload = () => {
    setStatus('idle');
    setErrorMessage('');
    setFileName('');
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  return (
    <div className="card mb-6">
      <h3 className="text-lg font-semibold text-brand-dark-600 mb-4">
        Upload Patient EHR Data
      </h3>

      <div
        className={`
          relative border-2 border-dashed rounded-lg p-8 text-center cursor-pointer
          transition-colors duration-200
          ${isDragActive
            ? 'border-brand-dark-500 bg-brand-beige-100'
            : 'border-brand-taupe-300 hover:border-brand-taupe-400 bg-brand-ivory-100'
          }
          ${status === 'error' ? 'border-red-300 bg-red-50' : ''}
          ${status === 'success' ? 'border-green-300 bg-green-50' : ''}
        `}
        onDragEnter={handleDragEnter}
        onDragLeave={handleDragLeave}
        onDragOver={handleDragOver}
        onDrop={handleDrop}
        onClick={status === 'idle' || status === 'error' ? handleClick : undefined}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if ((e.key === 'Enter' || e.key === ' ') && (status === 'idle' || status === 'error')) {
            handleClick();
          }
        }}
        aria-label="Upload XML file"
      >
        <input
          ref={fileInputRef}
          type="file"
          accept=".xml,.pdf"
          onChange={handleFileInput}
          className="hidden"
          aria-hidden="true"
        />

        {status === 'idle' && (
          <>
            <Upload className="mx-auto h-12 w-12 text-brand-taupe-400 mb-4" />
            <p className="text-brand-dark-600 font-medium mb-2">
              Drop your file here or click to browse
            </p>
            <p className="text-sm text-brand-taupe-500">
              Filename format: firstname_lastname.xml (e.g., john_smith.xml)
            </p>
          </>
        )}

        {status === 'uploading' && (
          <>
            <Loader2 className="mx-auto h-12 w-12 text-brand-dark-500 mb-4 animate-spin" />
            <p className="text-brand-dark-600 font-medium mb-2">
              Processing {fileName}...
            </p>
            <p className="text-sm text-brand-taupe-500">
              Parsing EHR data
            </p>
          </>
        )}

        {status === 'success' && (
          <>
            <CheckCircle className="mx-auto h-12 w-12 text-green-500 mb-4" />
            <p className="text-green-700 font-medium mb-2">
              Successfully imported patient data
            </p>
            <p className="text-sm text-green-600">
              {fileName}
            </p>
          </>
        )}

        {status === 'error' && (
          <>
            <AlertCircle className="mx-auto h-12 w-12 text-red-500 mb-4" />
            <p className="text-red-700 font-medium mb-2">
              Error processing file
            </p>
            <p className="text-sm text-red-600 mb-4">
              {errorMessage}
            </p>
            <button
              onClick={(e) => {
                e.stopPropagation();
                resetUpload();
              }}
              className="text-sm text-brand-dark-500 underline hover:text-brand-dark-600"
            >
              Try again
            </button>
          </>
        )}
      </div>

      <p className="mt-3 text-xs text-brand-taupe-500">
        <FileText className="inline h-3 w-3 mr-1" />
        Files must be named in format firstname_lastname.xml or firstname_lastname.pdf. Patient name will be extracted from filename.
      </p>
    </div>
  );
}
